import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

function parseAliases(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).map((item) => item.trim()).filter(Boolean);

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).map((item) => item.trim()).filter(Boolean);
  } catch {
    return [];
  }

  return [];
}

function parseAliasesText(value) {
  const seen = new Set();
  const aliases = [];

  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((alias) => {
      const key = alias.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      aliases.push(alias);
    });

  return aliases;
}

function formatGradeLabel(gradeLevel) {
  if (!gradeLevel) return '';
  return gradeLevel.canonical_name || gradeLevel.canonical_abbrev || `Grade ${gradeLevel.grade_code}`;
}

function buildDraft(existingGradeName, gradeLevel) {
  return {
    id: existingGradeName?.id || null,
    label: existingGradeName?.label || formatGradeLabel(gradeLevel),
    aliasesText: parseAliases(existingGradeName?.aliases).join(', '),
    is_active: existingGradeName ? Number(existingGradeName.is_active) !== 0 : true,
  };
}

export default function SchoolGradeAliasesField({ school, schoolId, token, disabled = false }) {
  const [gradeLevels, setGradeLevels] = useState([]);
  const [gradeNames, setGradeNames] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingGradeCode, setSavingGradeCode] = useState(null);
  const [savedGradeCode, setSavedGradeCode] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setSavedGradeCode(null);

    Promise.all([
      apiFetch('/grade-levels', {}, token),
      apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
    ])
      .then(([gradeLevelsRes, gradeNamesRes]) => {
        if (cancelled) return;
        setGradeLevels(gradeLevelsRes.data || []);
        setGradeNames(gradeNamesRes.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load grade aliases.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, token]);

  const gradeRows = useMemo(() => {
    const min = Number(school?.grade_min ?? -3);
    const max = Number(school?.grade_max ?? 12);

    return gradeLevels
      .filter((gradeLevel) => {
        const gradeCode = Number(gradeLevel.grade_code);
        return Number.isInteger(gradeCode) && gradeCode >= min && gradeCode <= max;
      })
      .sort((left, right) => Number(left.grade_code) - Number(right.grade_code));
  }, [gradeLevels, school?.grade_max, school?.grade_min]);

  useEffect(() => {
    const existingByGradeCode = new Map(
      gradeNames.map((gradeName) => [Number(gradeName.grade_code), gradeName])
    );

    const nextDrafts = {};
    gradeRows.forEach((gradeLevel) => {
      const gradeCode = Number(gradeLevel.grade_code);
      nextDrafts[gradeCode] = buildDraft(existingByGradeCode.get(gradeCode), gradeLevel);
    });
    setDrafts(nextDrafts);
  }, [gradeNames, gradeRows]);

  const updateDraft = (gradeCode, patch) => {
    setDrafts((current) => ({
      ...current,
      [gradeCode]: {
        ...(current[gradeCode] || {}),
        ...patch,
      },
    }));
    setSavedGradeCode(null);
    setRowErrors((current) => {
      const next = { ...current };
      delete next[gradeCode];
      return next;
    });
  };

  const saveGrade = async (gradeCode) => {
    if (disabled || savingGradeCode !== null) return;

    const draft = drafts[gradeCode];
    const label = String(draft?.label || '').trim();

    if (!label) {
      setRowErrors((current) => ({ ...current, [gradeCode]: 'Grade label is required.' }));
      return;
    }

    const payload = {
      school_id: Number(schoolId),
      grade_code: Number(gradeCode),
      label,
      aliases: parseAliasesText(draft.aliasesText),
      is_active: Boolean(draft.is_active),
    };

    setSavingGradeCode(gradeCode);
    setRowErrors((current) => {
      const next = { ...current };
      delete next[gradeCode];
      return next;
    });

    try {
      const res = await apiFetch(
        draft.id ? `/school-grade-names/${draft.id}` : '/school-grade-names',
        {
          method: draft.id ? 'PUT' : 'POST',
          body: JSON.stringify(payload),
        },
        token
      );
      const saved = res.data;

      setGradeNames((current) => {
        const withoutCurrent = current.filter((item) => Number(item.grade_code) !== Number(gradeCode));
        return [...withoutCurrent, saved].sort((left, right) => Number(left.grade_code) - Number(right.grade_code));
      });
      setSavedGradeCode(gradeCode);
    } catch (err) {
      setRowErrors((current) => ({
        ...current,
        [gradeCode]: err.message || 'Could not save this grade.',
      }));
    } finally {
      setSavingGradeCode(null);
    }
  };

  if (loading) {
    return <div className="ssp-grade-alias-empty">Loading grade aliases...</div>;
  }

  if (error) {
    return <div className="ssp-billing-error">{error}</div>;
  }

  if (gradeRows.length === 0) {
    return <div className="ssp-grade-alias-empty">No grade levels are configured for this school range.</div>;
  }

  return (
    <div className="ssp-grade-alias-field">
      <div className="ssp-grade-alias-head">
        <span>Canonical Grade</span>
        <span>Display Label</span>
        <span>Aliases</span>
        <span>Status</span>
        <span></span>
      </div>

      {gradeRows.map((gradeLevel) => {
        const gradeCode = Number(gradeLevel.grade_code);
        const draft = drafts[gradeCode] || buildDraft(null, gradeLevel);
        const isSaving = savingGradeCode === gradeCode;

        return (
          <div className="ssp-grade-alias-row" key={gradeCode}>
            <div className="ssp-grade-alias-canonical">
              <strong>{formatGradeLabel(gradeLevel)}</strong>
              <span>{gradeLevel.canonical_abbrev || `Code ${gradeCode}`}</span>
            </div>

            <label className="ssp-grade-alias-input">
              <span>Display Label</span>
              <input
                value={draft.label}
                onChange={(event) => updateDraft(gradeCode, { label: event.target.value })}
                disabled={disabled || isSaving}
              />
            </label>

            <label className="ssp-grade-alias-input">
              <span>Aliases</span>
              <input
                value={draft.aliasesText}
                onChange={(event) => updateDraft(gradeCode, { aliasesText: event.target.value })}
                disabled={disabled || isSaving}
                placeholder="K, Kinder, Kindergarten A"
              />
            </label>

            <label className="ssp-grade-alias-active">
              <input
                type="checkbox"
                checked={Boolean(draft.is_active)}
                onChange={(event) => updateDraft(gradeCode, { is_active: event.target.checked })}
                disabled={disabled || isSaving}
              />
              <span>Active</span>
            </label>

            <button
              type="button"
              className="sa-btn sa-btn-secondary ssp-grade-alias-save"
              onClick={() => saveGrade(gradeCode)}
              disabled={disabled || isSaving || savingGradeCode !== null}
            >
              {isSaving ? 'Saving...' : savedGradeCode === gradeCode ? 'Saved' : 'Save'}
            </button>

            {rowErrors[gradeCode] ? (
              <div className="ssp-grade-alias-error">{rowErrors[gradeCode]}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
